/**
 * Named specs. Developer-owned: the app ships one default per runner, and a
 * benchmark or a debug screen swaps them.
 *
 * The phone reads with `lines`, or `photo` when there is no OCR text
 * (`apps/ios/src/ai/remote-extraction-adapter.ts`), wherever its provider
 * offers a transport. `prod` is the reading it did before: the baseline the
 * bench measures every spec against, and what the providers without a
 * transport still do inside their own clients. The companion server's worker
 * reads with `local-lines`, or `local-photo`, on its small local model.
 */

import type { ResolvePolicy } from './resolve.js';
import type { Spec, SpecEntry } from './types.js';

/** The model's reading, corrected when the correction held together better. */
const MODEL = ['model-correct', 'model-read'];

/**
 * Production's precedence: the model's answer, whole, for every field it was
 * asked; the category follow-up only when the list did not fit the reading.
 * The OCR steps' candidates stay on record for the benchmark to judge but are
 * not chosen, except for an organisation number the model did not read.
 */
export const PROD_POLICY: ResolvePolicy = {
  default: { prefer: MODEL },
  orgNumber: { fill: [...MODEL, 'org-number'] },
  receiptCategory: { prefer: [...MODEL, 'model-categorize'] },
  otherCategories: { prefer: [...MODEL, 'model-categorize'] },
  itemCategories: { prefer: ['model-categorize', ...MODEL] },
};

const OCR_STEPS: SpecEntry[] = [
  { step: 'lines' },
  { step: 'org-number' },
  { step: 'dates' },
  { step: 'company', optional: true },
];

/**
 * The reading `callOpenAi` does, request for request: the photo and the OCR
 * text in one call that reads and files the receipt, a correcting second call
 * when the reading does not hold together, and a text-only filing call when
 * the category list did not fit the first.
 */
export const PROD_SPEC: Spec = [
  ...OCR_STEPS,
  { step: 'model-read', options: { in: ['image', 'ocr-text', 'categories'], ask: ['header', 'items', 'receipt-category', 'item-categories'] } },
  { step: 'resolve', id: 'resolve-read', options: { policy: PROD_POLICY } },
  {
    step: 'model-correct',
    when: 'invalid',
    optional: true,
    options: { of: 'model-read', in: ['image', 'ocr-text', 'categories'], ask: ['header', 'items', 'receipt-category', 'item-categories'] },
  },
  { step: 'resolve', id: 'resolve-corrected', options: { policy: PROD_POLICY } },
  { step: 'model-categorize', when: 'categories-dropped', optional: true, options: { in: ['items', 'categories'], ask: ['receipt-category', 'item-categories'] } },
  { step: 'resolve', options: { policy: PROD_POLICY } },
];

const SPLIT_POLICY: ResolvePolicy = {
  ...PROD_POLICY,
  receiptCategory: { prefer: ['model-categorize'] },
  otherCategories: { prefer: ['model-categorize'] },
  itemCategories: { prefer: ['model-categorize'] },
};

/**
 * Read first, file second: the reading call without the category list, then
 * a text-only filing call with the company's name and industry from the
 * register.
 */
export const SPLIT_SPEC: Spec = [
  ...OCR_STEPS,
  { step: 'model-read', options: { in: ['image', 'ocr-text'], ask: ['header', 'items'] } },
  { step: 'resolve', id: 'resolve-read', options: { policy: SPLIT_POLICY } },
  { step: 'model-correct', when: 'invalid', optional: true, options: { of: 'model-read', in: ['image', 'ocr-text'], ask: ['header', 'items'] } },
  { step: 'resolve', id: 'resolve-corrected', options: { policy: SPLIT_POLICY } },
  { step: 'model-categorize', options: { in: ['items', 'company', 'categories'], ask: ['receipt-category', 'item-categories'] } },
  { step: 'resolve', options: { policy: SPLIT_POLICY } },
];

/** {@link SPLIT_SPEC}, with the receipt filed by the company's industry where the register gives one. */
export const INDUSTRY_SPEC: Spec = [
  ...SPLIT_SPEC.slice(0, -1),
  { step: 'industry-category' },
  {
    step: 'resolve',
    options: {
      policy: {
        ...SPLIT_POLICY,
        receiptCategory: { prefer: ['industry-category', 'model-categorize'] },
      } satisfies ResolvePolicy,
    },
  },
];

/**
 * The model points at the product lines and the OCR supplies their amounts;
 * otherwise `prod`'s precedence. A correcting read copies amounts as `prod`'s
 * does, and wins when it is better.
 */
export const LINES_POLICY: ResolvePolicy = {
  ...PROD_POLICY,
  items: { prefer: ['model-correct', 'ground-items'] },
  itemCategories: { prefer: ['model-categorize', 'model-correct', 'ground-items'] },
};

/**
 * The phone's reading (idea 22's findings: fewer edits, better VAT, faster
 * and cheaper than `prod`): the photo and the numbered OCR lines in, the
 * header and an OCR line reference per product out, and `ground-items` reads
 * each amount off its line. When the reading does not hold together,
 * `prod`'s correcting read; when the category list did not fit, `prod`'s
 * filing call with the list described (`describedCategories`) and the
 * compact answer (`compactCategorySchema`).
 */
export const LINES_SPEC: Spec = [
  { step: 'lines' },
  { step: 'org-number' },
  {
    step: 'model-read',
    options: { in: ['image', 'lines', 'categories'], ask: ['header', 'item-refs', 'receipt-category', 'item-categories'] },
  },
  { step: 'ground-items', options: { of: 'model-read' } },
  { step: 'resolve', id: 'resolve-read', options: { policy: LINES_POLICY } },
  {
    step: 'model-correct',
    when: 'invalid',
    optional: true,
    options: { of: 'model-read', in: ['image', 'ocr-text', 'categories'], ask: ['header', 'items', 'receipt-category', 'item-categories'] },
  },
  { step: 'resolve', id: 'resolve-corrected', options: { policy: LINES_POLICY } },
  {
    step: 'model-categorize',
    when: 'categories-dropped',
    optional: true,
    options: { in: ['items', 'categories', 'described'], ask: ['receipt-category', 'item-categories'], format: 'compact' },
  },
  { step: 'resolve', options: { policy: LINES_POLICY } },
];

/**
 * The photo alone, for a receipt with no OCR text to point at: `prod` without
 * its OCR steps and block, which is what `callOpenAi` sends without text, and
 * its filing call with the list described as `lines`' is.
 */
export const PHOTO_SPEC: Spec = [
  { step: 'model-read', options: { in: ['image', 'categories'], ask: ['header', 'items', 'receipt-category', 'item-categories'] } },
  { step: 'resolve', id: 'resolve-read', options: { policy: PROD_POLICY } },
  {
    step: 'model-correct',
    when: 'invalid',
    optional: true,
    options: { of: 'model-read', in: ['image', 'categories'], ask: ['header', 'items', 'receipt-category', 'item-categories'] },
  },
  { step: 'resolve', id: 'resolve-corrected', options: { policy: PROD_POLICY } },
  {
    step: 'model-categorize',
    when: 'categories-dropped',
    optional: true,
    options: { in: ['items', 'categories', 'described'], ask: ['receipt-category', 'item-categories'], format: 'compact' },
  },
  { step: 'resolve', options: { policy: PROD_POLICY } },
];

/**
 * A small local model's precedence (the server worker's): `lines`', with
 * every category from the separate filing call.
 */
export const LOCAL_POLICY: ResolvePolicy = {
  ...LINES_POLICY,
  receiptCategory: { prefer: ['model-categorize'] },
  otherCategories: { prefer: ['model-categorize'] },
  itemCategories: { prefer: ['model-categorize'] },
};

/**
 * `lines` for a small local model, the companion server's worker
 * (`qwen3-vl:4b-instruct`): read without the category list, then filed in a
 * second, text-only call. In the same call as the reading, a 4B model files
 * receipts far worse (60% against 77%, item main groups 31% against 49%,
 * idea 22's bench, 2026-10-07); a 32B model files them better there.
 */
export const LOCAL_LINES_SPEC: Spec = [
  { step: 'lines' },
  { step: 'org-number' },
  { step: 'model-read', options: { in: ['image', 'lines'], ask: ['header', 'item-refs'] } },
  { step: 'ground-items', options: { of: 'model-read' } },
  { step: 'resolve', id: 'resolve-read', options: { policy: LOCAL_POLICY } },
  { step: 'model-correct', when: 'invalid', optional: true, options: { of: 'model-read', in: ['image', 'ocr-text'], ask: ['header', 'items'] } },
  { step: 'resolve', id: 'resolve-corrected', options: { policy: LOCAL_POLICY } },
  // Optional: a filing call that fails leaves the reading standing, without categories.
  { step: 'model-categorize', optional: true, options: { in: ['items', 'categories'], ask: ['receipt-category', 'item-categories'] } },
  { step: 'resolve', options: { policy: LOCAL_POLICY } },
];

/** `photo`'s precedence, with every category from the separate filing call. */
const LOCAL_PHOTO_POLICY: ResolvePolicy = {
  ...PROD_POLICY,
  receiptCategory: { prefer: ['model-categorize'] },
  otherCategories: { prefer: ['model-categorize'] },
  itemCategories: { prefer: ['model-categorize'] },
};

/**
 * The photo alone for a small local model, for a receipt with no OCR text:
 * production's compact instructions, then filed in a call of its own - as
 * the worker read every receipt before it read by the lines, when its 8k
 * window never had room for the category list.
 */
export const LOCAL_PHOTO_SPEC: Spec = [
  { step: 'model-read', options: { in: ['image'], ask: ['header', 'items'], prompt: 'compact' } },
  { step: 'resolve', id: 'resolve-read', options: { policy: LOCAL_PHOTO_POLICY } },
  { step: 'model-correct', when: 'invalid', optional: true, options: { of: 'model-read', in: ['image'], ask: ['header', 'items'] } },
  { step: 'resolve', id: 'resolve-corrected', options: { policy: LOCAL_PHOTO_POLICY } },
  // Optional: a filing call that fails leaves the reading standing, without categories.
  { step: 'model-categorize', optional: true, options: { in: ['items', 'categories'], ask: ['receipt-category', 'item-categories'] } },
  { step: 'resolve', options: { policy: LOCAL_PHOTO_POLICY } },
];

/**
 * What is read with no model at all: the store's name, the date, the total,
 * the organisation number and the VAT table, each off the OCR text by rules.
 * The phone runs it after OCR on every scan, so a receipt has these fields
 * whatever reader - if any - comes after (idea 25). No items, no categories.
 */
export const RULES_POLICY: ResolvePolicy = {
  merchant: { fill: ['store-name'] },
  orgNumber: { checksum: ['org-number'] },
  purchasedAt: { fill: ['dates'] },
  total: { fill: ['total-vote'] },
  vatLines: { fill: ['vat'] },
};

export const RULES_SPEC: Spec = [
  { step: 'lines' },
  { step: 'org-number' },
  { step: 'dates' },
  { step: 'total-vote' },
  { step: 'vat' },
  { step: 'store-name' },
  { step: 'resolve', options: { policy: RULES_POLICY } },
];

export const READING_SPECS: Record<string, Spec> = {
  prod: PROD_SPEC,
  lines: LINES_SPEC,
  photo: PHOTO_SPEC,
  split: SPLIT_SPEC,
  industry: INDUSTRY_SPEC,
  rules: RULES_SPEC,
  'local-lines': LOCAL_LINES_SPEC,
  'local-photo': LOCAL_PHOTO_SPEC,
};

/** `spec` with `entry` inserted after the step with id `after`: for a runner's own steps. */
export function withStepAfter(spec: Spec, after: string, entry: SpecEntry): Spec {
  const index = spec.findIndex((existing) => (existing.id ?? existing.step) === after);
  if (index < 0) throw new Error(`no step "${after}" in the spec`);
  return [...spec.slice(0, index + 1), entry, ...spec.slice(index + 1)];
}
